import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import type { PlatformRecurring, RecurringTask } from "../../../api/client.ts";
import { formatNumber } from "../../../utils/format.ts";
import Panel from "../../Panel.tsx";
import RelativeTime from "../../RelativeTime.tsx";
import StatusChip from "../../StatusChip.tsx";
import ScrollTable from "../../table/ScrollTable.tsx";
import { seconds, taskKindLabel } from "./recurringFormat.ts";

const num = { fontVariantNumeric: "tabular-nums" } as const;

/**
 * The durable-task runner: per kind of long work, what waits, what runs, and
 * how the last day went; then the tasks running now and the latest failures.
 */
export default function DurableTasksPanel({ data }: { data: PlatformRecurring }) {
  const runners = data.runners.length;
  return (
    <>
      <Panel
        title="Durable tasks"
        subtitle={
          runners
            ? `Long work the task runners do for Restate · ${formatNumber(runners)} ${runners === 1 ? "runner" : "runners"} busy`
            : "Long work the task runners do for Restate"
        }
        count={data.task_kinds.length}
        disablePadding
      >
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Kind</TableCell>
              <TableCell align="right">Waiting</TableCell>
              <TableCell align="right">Longest wait</TableCell>
              <TableCell align="right">Running</TableCell>
              <TableCell align="right">Done, 24h</TableCell>
              <TableCell align="right">Failed, 24h</TableCell>
              <TableCell align="right">Slowest 5%</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.task_kinds.map((k) => (
              <TableRow key={k.kind}>
                <TableCell>{taskKindLabel(k.kind)}</TableCell>
                <TableCell align="right" sx={num}>
                  {formatNumber(k.queued)}
                </TableCell>
                <TableCell align="right" sx={num}>
                  {seconds(k.oldest_queued_s)}
                </TableCell>
                <TableCell align="right" sx={num}>
                  {formatNumber(k.running)}
                </TableCell>
                <TableCell align="right" sx={num}>
                  {formatNumber(k.succeeded_24h)}
                </TableCell>
                <TableCell align="right" sx={{ ...num, color: k.failed_24h ? "error.main" : "text.secondary" }}>
                  {k.failed_24h ? formatNumber(k.failed_24h) : "–"}
                </TableCell>
                <TableCell align="right" sx={num}>
                  {seconds(k.p95_s)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </ScrollTable>
      </Panel>
      {data.running_tasks.length > 0 && <RunningTasksPanel tasks={data.running_tasks} />}
      {data.recent_failures.length > 0 && (
        <Panel title="Recent task failures" count={data.recent_failures.length} disablePadding>
          <ScrollTable>
            <TableHead>
              <TableRow>
                <TableCell>Kind</TableCell>
                <TableCell>Error</TableCell>
                <TableCell align="right">Attempts</TableCell>
                <TableCell>When</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.recent_failures.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>
                    {taskKindLabel(f.kind)}
                    {f.status === "cancelled" && (
                      <Typography variant="caption" component="div" sx={{ color: "text.secondary" }}>
                        Cancelled
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 420, wordBreak: "break-word" }}>{f.error ?? "–"}</TableCell>
                  <TableCell align="right" sx={num}>
                    {formatNumber(f.attempt)}
                  </TableCell>
                  <TableCell>
                    <RelativeTime value={f.finished_at} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ScrollTable>
        </Panel>
      )}
    </>
  );
}

function RunningTasksPanel({ tasks }: { tasks: RecurringTask[] }) {
  return (
    <Panel title="Running now" count={tasks.length} disablePadding>
      <TaskTable tasks={tasks} />
    </Panel>
  );
}

/** Tasks with their attempt, heartbeat and runner; also in a job's drawer. */
export function TaskTable({ tasks }: { tasks: RecurringTask[] }) {
  return (
    <ScrollTable>
      <TableHead>
        <TableRow>
          <TableCell>Kind</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Started</TableCell>
          <TableCell>Last heartbeat</TableCell>
          <TableCell align="right">Attempt</TableCell>
          <TableCell>Runner</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {tasks.map((t) => (
          <TableRow key={t.id}>
            <TableCell>
              {taskKindLabel(t.kind)}
              {t.lock_key && (
                <Typography variant="caption" component="div" sx={{ color: "text.secondary" }}>
                  Lock {t.lock_key}
                </Typography>
              )}
            </TableCell>
            <TableCell>
              <StatusChip kind="job" value={t.status} />
            </TableCell>
            <TableCell>
              <RelativeTime value={t.started_at} never="Not yet" />
            </TableCell>
            <TableCell>
              <RelativeTime value={t.heartbeat_at} never="None yet" />
            </TableCell>
            <TableCell align="right" sx={num}>
              {t.attempt} of {t.max_attempts}
            </TableCell>
            <TableCell sx={{ color: "text.secondary" }}>{t.lease_owner ?? "–"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </ScrollTable>
  );
}
